import type { AxiosRequestConfig } from 'axios';
import { HttpService } from '@/features/api/http.service';

interface SSEOptions extends AxiosRequestConfig {
  url: string;
}

interface SSEHandlers<T> {
  onData(data: T, stop: () => void): void;
  onError(error: Error, stop: () => void): void;
  onConnect?(): void;
  onDisconnect?(): void;
}

export interface SSEController {
  stop(): void;
}

const DATA_PREFIX = 'event: data\ndata: ';
const ERROR_PREFIX = 'event: error\ndata: ';

function parseErrorMessage(raw: string): string {
  try {
    const message = JSON.parse(raw)?.message;
    return typeof message === 'string' && message ? message : 'SSE stream error';
  } catch {
    return 'SSE stream error';
  }
}

export function subscribeToSse<T>(
  options: SSEOptions,
  handlers: SSEHandlers<T>,
  reconnectDelayMs = 0,
): SSEController {
  const controller = new AbortController();
  let stopped = false;

  function stop() {
    stopped = true;
    controller.abort();
  }

  async function connect() {
    try {
      const stream = await HttpService.get<string, ReadableStream>(options.url, undefined, {
        headers: {
          ...options.headers,
          Accept: 'text/event-stream',
          // 'Cache-Control': 'no-cache',
        },
        responseType: 'stream',
        adapter: 'fetch',
        signal: controller.signal,
      });

      const reader = stream.pipeThrough(new TextDecoderStream()).getReader();

      if (handlers.onConnect) handlers.onConnect();

      // A read returns whatever arrived, not whole events: a frame can span two reads and two
      // frames can share one. So completed events are taken off a buffer and the remainder is
      // kept for the next read — splitting each chunk on its own drops every frame that straddles
      // a boundary, which the largest payloads do most often.
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          // The server closed without a terminal frame (timeout, pod shutdown): the watch is lost.
          if (!stopped) handlers.onError(new Error('SSE stream closed'), stop);
          break;
        }

        buffer += value as string;

        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
          const message = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          boundary = buffer.indexOf('\n\n');

          // The server closes the stream right after an error frame, so it is terminal for the caller.
          if (message.startsWith(ERROR_PREFIX)) {
            handlers.onError(
              new Error(parseErrorMessage(message.substring(ERROR_PREFIX.length))),
              stop,
            );
            stop();
            return;
          }
          if (!message.startsWith(DATA_PREFIX)) continue;
          try {
            const eventData = JSON.parse(message.substring(DATA_PREFIX.length));
            handlers.onData(eventData, stop);
          } catch {
            // ignore parse errors; upstream format issue
          }
          // A terminal frame may share its read with later frames: nothing after it counts.
          if (stopped) return;
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        handlers.onError(err instanceof Error ? err : new Error(String(err)), stop);
        if (!stopped && reconnectDelayMs > 0) setTimeout(connect, reconnectDelayMs);
      }
    } finally {
      if (handlers.onDisconnect) handlers.onDisconnect();
    }
  }

  connect();

  return { stop };
}
